/**
 * Пикер эмодзи: обычные и премиальные.
 *
 * Обычные — юникод-символы, вставляются как текст. Премиальные (Telegram
 * Premium) — картинки с идентификатором: их набор грузится с аккаунта и
 * вставляется как сущность через onPickCustom. Отправлять премиальные может
 * только Premium-аккаунт; видеть и класть в заготовку — кто угодно.
 */

import { useEffect, useRef, useState } from "react";
import type { CustomEmojiSetDto } from "../lib/api.js";
import { useSession } from "../state/session.tsx";
import { SmileIcon } from "./icons.tsx";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface EmojiPickerProps {
  /** Вставить обычный эмодзи (текст). */
  onPick: (emoji: string) => void;
  /** Вставить премиальный эмодзи (сущность). Нет — раздел премиум скрыт. */
  onPickCustom?: (customEmojiId: string, emoji: string, src: string) => void;
  /** Аккаунт, чьи премиальные наборы показывать. */
  accountId?: string | null;
  disabled?: boolean;
  accountIsPremium?: boolean | null;
  /**
   * Куда раскрывать список. По умолчанию вверх (композер прижат к низу).
   * В модальном окне — вниз: вверх список вылез бы за карточку, поверх
   * оверлея Radix, и тот перехватывал бы клики по эмодзи.
   */
  direction?: "up" | "down";
}

const RECENT_KEY = "tw.emoji.recent";
const RECENT_LIMIT = 24;

const GROUPS: Array<{ title: string; items: string[] }> = [
  {
    title: "Часто нужные",
    items: [
      "👍","🙏","👌","🤝","✅","❌","⚠️","❗","❓","💯","🔥","⭐",
      "📌","📎","📄","📆","⏰","💰","💳","📞","✉️","🚀","🎯","🎉",
    ],
  },
  {
    title: "Смайлы",
    items: [
      "🙂","😊","😀","😃","😄","😁","😆","😅","😂","🤣","😉","😍",
      "🥰","😘","😗","😙","😚","🤗","🤩","🤔","🤨","😐","😑","😶",
      "🙄","😏","😣","😥","😮","🤐","😯","😪","😫","🥱","😴","😌",
      "😛","😜","😝","🤤","😒","😓","😔","🙁","☹️","😖","😞","😟",
      "😢","😭","😤","😠","😡","🤬","🤯","😳","🥵","🥶","😱","😨",
      "😰","😥","🤥","😬","🤢","🤮","🤧","😷","🤒","🤕","🥳","😎",
      "🤓","🧐","🤠","🥴","😈","👿","💀","👻","🤡","😺","😻","🙀",
    ],
  },
  {
    title: "Жесты и люди",
    items: [
      "👋","🤚","✋","🖐️","👌","🤌","🤏","✌️","🤞","🤟","🤘","🤙",
      "👈","👉","👆","👇","☝️","👍","👎","✊","👊","🤛","🤜","👏",
      "🙌","👐","🤲","🙏","✍️","💪","🦾","👀","👁️","👤","👥","🗣️",
      "❤️","🧡","💛","💚","💙","💜","🖤","🤍","🤎","💔","❣️","💕",
      "💞","💓","💗","💖","💘","💝","💟","♥️","💋","💌","💤","💢",
    ],
  },
  {
    title: "Работа и вещи",
    items: [
      "💼","🏢","🏭","🏗️","🔧","🔩","⚙️","🧰","🛠️","📊","📈","📉",
      "🗂️","📁","📂","📋","📝","🖊️","🖋️","✏️","📐","📏","🔍","🔎",
      "🖥️","💻","⌨️","🖨️","🖱️","🔌","🔋","📱","☎️","📟","📠","💡",
      "💵","💴","💶","💷","🪙","💳","🧾","🛒","🎁","📦","🚚","🚛",
      "🚗","🚕","✈️","🛫","🚢","⛴️","🕐","📅","🗓️","🔔","🔕","📢",
    ],
  },
  {
    title: "Символы",
    items: [
      "✅","☑️","✔️","❌","❎","➕","➖","➗","✖️","🟰","❓","❔",
      "❗","❕","‼️","⁉️","💯","🔝","🔙","🔜","🆗","🆕","🆒","🔴",
      "🟠","🟡","🟢","🔵","🟣","⚫","⚪","🟥","🟧","🟨","🟩","🟦",
      "⭐","🌟","✨","⚡","🔥","💥","💫","☀️","🌙","☁️","❄️","⏳",
    ],
  },
];

export function EmojiPicker({
  onPick,
  onPickCustom,
  accountId,
  disabled,
  accountIsPremium,
  direction = "up",
}: EmojiPickerProps) {
  const { api } = useSession();
  const [open, setOpen] = useState(false);
  const [recent, setRecent] = useState<string[]>(() => readRecent());
  const [customSets, setCustomSets] = useState<CustomEmojiSetDto[] | null>(null);
  const boxRef = useRef<HTMLDivElement>(null);

  const premiumEnabled = Boolean(onPickCustom && accountId);

  useEffect(() => {
    if (!open || !premiumEnabled || customSets !== null || !accountId) return;
    api
      .customEmojiSets(accountId)
      .then(({ sets }) => setCustomSets(sets))
      .catch(() => setCustomSets([]));
  }, [open, premiumEnabled, customSets, accountId, api]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent): void => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const pick = (emoji: string): void => {
    onPick(emoji);
    const next = [emoji, ...recent.filter((e) => e !== emoji)].slice(0, RECENT_LIMIT);
    setRecent(next);
    try {
      globalThis.localStorage?.setItem(RECENT_KEY, JSON.stringify(next));
    } catch {
      /* приватный режим — история просто не сохранится */
    }
  };

  return (
    <div className="absolute bottom-1.5 right-1.5" ref={boxRef}>
      <Button
        variant="ghost"
        size="icon-sm"
        className="rounded-full text-muted-foreground hover:text-brand"
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => setOpen((v) => !v)}
        disabled={disabled}
        aria-expanded={open}
        aria-label="Эмодзи"
        title="Эмодзи"
      >
        <SmileIcon className="size-[18px]" />
      </Button>

      {open && (
        <div
          className={cn(
            "absolute right-0 z-30 max-h-[360px] w-[340px] overflow-y-auto rounded-2xl border border-border bg-popover p-2 shadow-2xl",
            direction === "down" ? "top-[calc(100%+10px)]" : "bottom-[calc(100%+10px)]",
          )}
        >
          {premiumEnabled && (
            <section className="mb-1">
              <h4 className="mb-1.5 text-[10.5px] font-semibold uppercase tracking-[0.06em] text-brand">
                Премиальные
              </h4>
              {customSets === null && (
                <p className="px-1 py-2 text-[11px] text-muted-foreground">Загрузка…</p>
              )}
              {customSets?.length === 0 && (
                <p className="rounded-lg bg-muted p-2 text-[11px] leading-relaxed text-muted-foreground">
                  У аккаунта нет наборов премиальных эмодзи. Их добавляют в самом
                  Telegram; здесь появятся установленные.
                </p>
              )}
              {customSets?.map((set) => (
                <div key={set.id} className="mb-2">
                  <p className="mb-1 truncate text-[10px] text-muted-foreground">{set.title}</p>
                  <div className="grid grid-cols-8 gap-0.5">
                    {set.items.map((item) => {
                      const src = `/api/custom-emoji/${encodeURIComponent(item.customEmojiId)}`;
                      return (
                        <button
                          key={item.customEmojiId}
                          type="button"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => onPickCustom?.(item.customEmojiId, item.emoji, src)}
                          className="grid aspect-square place-items-center rounded-lg p-1 transition-transform hover:scale-110 hover:bg-accent"
                          title={item.emoji}
                        >
                          <img src={src} alt={item.emoji} loading="lazy" className="size-6" />
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
              <div className="my-2 h-px bg-border" />
            </section>
          )}

          {recent.length > 0 && (
            <section>
              <h4 className="mb-1.5 text-[10.5px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
                Недавние
              </h4>
              <div className="grid grid-cols-8 gap-0.5">
                {recent.map((e) => (
                  <button
                    key={`r-${e}`}
                    type="button"
                    onMouseDown={(ev) => ev.preventDefault()}
                    onClick={() => pick(e)}
                    className="grid aspect-square place-items-center rounded-lg text-xl leading-none transition-transform hover:scale-115 hover:bg-accent"
                  >
                    {e}
                  </button>
                ))}
              </div>
            </section>
          )}

          {accountIsPremium === false && premiumEnabled && (
            <p className="my-2 rounded-lg bg-muted p-2 text-[11px] leading-relaxed text-muted-foreground">
              Отправлять премиальные эмодзи можно только с аккаунта с подпиской
              Telegram Premium.
            </p>
          )}

          {GROUPS.map((group) => (
            <section key={group.title} className="mt-3.5 first:mt-0">
              <h4 className="mb-1.5 text-[10.5px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
                {group.title}
              </h4>
              <div className="grid grid-cols-8 gap-0.5">
                {group.items.map((e, i) => (
                  <button
                    key={`${e}-${i}`}
                    type="button"
                    onMouseDown={(ev) => ev.preventDefault()}
                    onClick={() => pick(e)}
                    className="grid aspect-square place-items-center rounded-lg text-xl leading-none transition-transform hover:scale-115 hover:bg-accent"
                  >
                    {e}
                  </button>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}

function readRecent(): string[] {
  try {
    const raw = globalThis.localStorage?.getItem(RECENT_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed)
      ? parsed.filter((e): e is string => typeof e === "string")
      : [];
  } catch {
    return [];
  }
}
